/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * Firma (Traccar User): uetds + sınırsız uetdsCredentials (PUT /api/users/{id}).
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Text,
  Spinner,
  HStack,
  Divider,
  Box,
  useToast,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";

function rowsFromApi(data) {
  const list = Array.isArray(data?.uetdsCredentials) ? data.uetdsCredentials : [];
  return list.map((c, idx) => ({
    key: `c-${c?.id ?? "n"}-${idx}`,
    id: c?.id ?? null,
    username: typeof c?.username === "string" ? c.username : "",
    passwordSet: Boolean(c?.uetdsPasswordSet),
    password: "",
  }));
}

export default function FirmaUetdsModal({ isOpen, onClose, firma }) {
  const toast = useToast();
  const { handleError } = useErrorHandler();
  const userId = firma?.HspId ?? firma?.id;
  const firmaAdı =
    firma?.firma_ismi || firma?.firma || firma?.name || "Firma";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uetds, setUetds] = useState(false);
  const [credRows, setCredRows] = useState([]);
  const [vehicleUetds, setVehicleUetds] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  const resetForm = () => {
    setUetds(false);
    setCredRows([]);
    setVehicleUetds([]);
  };

  const loadVehicleUetds = useCallback(async () => {
    if (!userId) return;
    setVehiclesLoading(true);
    try {
      const res = await api.get(`/api/firmalar/${userId}/uetds-konum-ozeti`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Araç UETDS özeti alınamadı");
      }
      setVehicleUetds(Array.isArray(data) ? data : []);
    } catch (e) {
      handleError(e, "Araç konum bildirimi listesi");
      setVehicleUetds([]);
    } finally {
      setVehiclesLoading(false);
    }
  }, [userId, handleError]);

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/users/${userId}`);
      const data = await res.json();
      setUetds(Boolean(data.uetds));
      setCredRows(rowsFromApi(data));
    } catch (e) {
      handleError(e, "UETDS kullanıcı yükleme");
      resetForm();
      onClose?.();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose sadece hata için
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      loadUser();
    } else if (!isOpen) {
      resetForm();
    }
  }, [isOpen, userId, loadUser]);

  useEffect(() => {
    if (isOpen && userId && uetds && !loading) {
      loadVehicleUetds();
    } else if (!uetds) {
      setVehicleUetds([]);
    }
  }, [isOpen, userId, uetds, loading, loadVehicleUetds]);

  const konumDurumLabel = (uetdsVal) =>
    uetdsVal === true
      ? { text: "Açık", color: "green.600" }
      : { text: "Kapalı", color: "gray.500" };

  const addRow = () => {
    setCredRows((r) => [
      ...r,
      {
        key: `new-${Date.now()}-${Math.random()}`,
        id: null,
        username: "",
        passwordSet: false,
        password: "",
      },
    ]);
  };

  const removeRowAt = (index) => {
    setCredRows((r) => r.filter((_, i) => i !== index));
  };

  const updateRow = (index, patch) => {
    setCredRows((r) => r.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    if (!userId) return;

    if (uetds) {
      if (credRows.length === 0) {
        toast({
          title: "Uyarı",
          description: "UETDS açıkken en az bir UETDS hesabı (kullanıcı adı) gerekli.",
          status: "warning",
          duration: 4500,
          isClosable: true,
        });
        return;
      }
      for (let i = 0; i < credRows.length; i += 1) {
        const row = credRows[i];
        const u = (row.username || "").trim();
        if (!u) {
          toast({
            title: "Uyarı",
            description: `${i + 1}. satırda kullanıcı adı eksik.`,
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        if (row.id == null && !(row.password || "").trim()) {
          toast({
            title: "Uyarı",
            description: `${i + 1}. satır: yeni hesap için şifre gerekli.`,
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
      }
    }

    setSaving(true);
    try {
      if (!uetds) {
        const res = await api.put(`/api/users/${userId}`, { uetds: false });
        await res.json();
      } else {
        const uetdsCredentials = credRows.map((row) => {
          const o = { username: row.username.trim() };
          if (row.id != null) o.id = row.id;
          const p = (row.password || "").trim();
          if (p) o.uetdsPassword = p;
          return o;
        });
        const res = await api.put(`/api/users/${userId}`, { uetds: true, uetdsCredentials });
        const data = await res.json();
        setCredRows(rowsFromApi(data));
      }
      toast({
        title: "UETDS güncellendi",
        description: firmaAdı,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose?.();
    } catch (e) {
      handleError(e, "UETDS kaydı");
    } finally {
      setSaving(false);
    }
  };

  const handleModalClose = () => {
    if (!saving) onClose?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} isCentered scrollBehavior="inside" size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="md" pb={1}>
          UETDS — {firmaAdı}
        </ModalHeader>
        <ModalCloseButton isDisabled={saving} />
        <ModalBody pb={2} pt={1}>
          {loading ? (
            <HStack py={6} justify="center">
              <Spinner size="md" />
            </HStack>
          ) : (
            <VStack align="stretch" spacing={2}>
              <Text fontSize="xs" color="gray.600" lineHeight="1.35">
                Her satır bağımsız bir UETDS web servis hesabıdır (sınırsız ekleyebilirsiniz). Kayıtlar Traccar
                kullanıcı nesnesindedir; araç için hangi kayıt kullanılacağı &quot;Konum bildirimi&quot; ekranından seçilir.
              </Text>
              <FormControl display="flex" alignItems="center" justifyContent="space-between" mb={0}>
                <FormLabel htmlFor="uetds-sw" mb="0" fontSize="sm">
                  UETDS kullan (bu firma)
                </FormLabel>
                <Switch
                  id="uetds-sw"
                  isChecked={uetds}
                  onChange={(e) => setUetds(e.target.checked)}
                  colorScheme="blue"
                />
              </FormControl>

              {uetds && (
                <>
                  <Divider my={1} />
                  <HStack justify="space-between" align="center" spacing={2}>
                    <Text fontSize="sm" fontWeight="600">
                      UETDS hesapları
                    </Text>
                    <Button size="xs" variant="outline" onClick={addRow}>
                      Hesap ekle
                    </Button>
                  </HStack>

                  {credRows.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" py={0}>
                      Henüz hesap yok — &quot;Hesap ekle&quot; ile kullanıcı adı ve şifre girin.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {credRows.map((row, index) => (
                        <Box
                          key={row.key}
                          p={2}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor="gray.200"
                          bg="gray.50"
                        >
                          <HStack justify="space-between" align="center" mb={1}>
                            <Text fontSize="xs" fontWeight="600" color="gray.600">
                              Hesap {index + 1}
                            </Text>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              height="auto"
                              minH={0}
                              py={0.5}
                              leftIcon={<DeleteIcon boxSize={3} />}
                              onClick={() => removeRowAt(index)}
                            >
                              Sil
                            </Button>
                          </HStack>
                          <FormControl mb={1}>
                            <FormLabel fontSize="xs" mb={0.5}>
                              Web servisi kullanıcı adı
                            </FormLabel>
                            <Input
                              size="sm"
                              value={row.username}
                              onChange={(e) => updateRow(index, { username: e.target.value })}
                              placeholder="UETDS kullanıcı adı"
                              autoComplete="off"
                              bg="white"
                            />
                          </FormControl>
                          <FormControl mb={0}>
                            <FormLabel fontSize="xs" mb={0.5}>
                              Web servisi şifresi
                              {row.passwordSet ? (
                                <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal" ml={1}>
                                  Kayıtlı — yalnızca değiştirmek için yeni şifre
                                </Text>
                              ) : row.id != null ? null : (
                                <Text as="span" fontSize="xs" color="red.500" fontWeight="normal" ml={1}>
                                  Zorunlu
                                </Text>
                              )}
                            </FormLabel>
                            <Input
                              type="password"
                              size="sm"
                              value={row.password}
                              onChange={(e) => updateRow(index, { password: e.target.value })}
                              placeholder={
                                row.passwordSet ? "Boş bırak = değiştirme" : "Şifre"
                              }
                              autoComplete="new-password"
                              bg="white"
                            />
                          </FormControl>
                        </Box>
                      ))}
                    </VStack>
                  )}

                  <Divider my={2} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" mb={2}>
                      Konum bildirimi
                      {!vehiclesLoading && vehicleUetds.length > 0
                        ? ` (${vehicleUetds.length} araç)`
                        : ""}
                    </Text>
                    {vehiclesLoading ? (
                      <HStack py={2} justify="center">
                        <Spinner size="sm" />
                      </HStack>
                    ) : vehicleUetds.length === 0 ? (
                      <Text fontSize="xs" color="gray.500">
                        Aktif araç yok.
                      </Text>
                    ) : (
                      <>
                        <Box
                          maxH="108px"
                          overflowY="auto"
                          borderWidth="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                          bg="gray.50"
                        >
                          {vehicleUetds.map((v, idx) => {
                            const durum = konumDurumLabel(v.uetds);
                            return (
                              <HStack
                                key={v.id}
                                h="36px"
                                px={3}
                                justify="space-between"
                                borderBottom={
                                  idx < vehicleUetds.length - 1
                                    ? "1px solid"
                                    : "none"
                                }
                                borderColor="gray.200"
                              >
                                <Text fontSize="sm" noOfLines={1} flex={1} mr={2}>
                                  {v.plaka || v.imei || `#${v.id}`}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  fontWeight="medium"
                                  color={durum.color}
                                  flexShrink={0}
                                >
                                  {durum.text}
                                </Text>
                              </HStack>
                            );
                          })}
                        </Box>
                        {vehicleUetds.length > 3 && (
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            Daha fazla araç için kaydırın.
                          </Text>
                        )}
                      </>
                    )}
                  </Box>
                </>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2} py={3}>
          <Button variant="ghost" onClick={handleModalClose} isDisabled={saving} size="sm">
            Kapat
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={saving} isDisabled={loading} size="sm">
            Kaydet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
